import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Modal, StatusBadge, Field, inputCls, thCls, tdCls } from "./ui";

const STATUSES = ["active", "coming_soon", "hidden", "disabled"];
const EMPTY = { name: "", code: "", currency: "USD", status: "coming_soon", states: [] };

export default function Locations() {
  const [countries, setCountries] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [newState, setNewState] = useState("");
  const [cityInputs, setCityInputs] = useState({});

  const load = () => api.get("/admin/countries").then((r) => setCountries(r.data || [])).catch(() => setCountries([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (c, status) => {
    await api.put(`/admin/countries/${c.id}`, { ...c, status }).catch((e) => toast.error(formatApiError(e)));
    toast.success(`${c.name} → ${status.replace("_", " ")}`);
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from the platform?`)) return;
    await api.delete(`/admin/countries/${c.id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/countries", form);
      else await api.put(`/admin/countries/${editing}`, form);
      toast.success("Location saved.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addState = () => {
    if (!newState.trim()) return;
    setForm((f) => ({ ...f, states: [...f.states, { name: newState.trim(), cities: [] }] }));
    setNewState("");
  };

  const addCity = (stateName) => {
    const v = (cityInputs[stateName] || "").trim();
    if (!v) return;
    setForm((f) => ({
      ...f,
      states: f.states.map((s) => (s.name === stateName ? { ...s, cities: [...s.cities, v] } : s)),
    }));
    setCityInputs((c) => ({ ...c, [stateName]: "" }));
  };

  return (
    <div data-testid="admin-locations">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-stone2 max-w-xl">Switch countries on or off, and mark them Active, Coming Soon, Hidden, or Disabled. Each country supports its own states/regions and cities.</p>
        <button onClick={() => { setForm(EMPTY); setEditing("new"); }} className="inline-flex items-center gap-2 bg-ruby text-cream text-sm font-semibold px-5 py-2.5 rounded-xl shrink-0" data-testid="new-country-btn">
          <Plus className="w-4 h-4" /> Add country
        </button>
      </div>
      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
        <table className="w-full text-left min-w-[680px]" data-testid="countries-table">
          <thead className="border-b border-sand">
            <tr><th className={thCls}>Country</th><th className={thCls}>Code</th><th className={thCls}>Currency</th><th className={thCls}>Regions / Cities</th><th className={thCls}>Status</th><th className={thCls}>Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-sand">
            {(countries || []).map((c) => (
              <tr key={c.id} data-testid={`country-row-${c.code}`}>
                <td className={`${tdCls} font-semibold`}>{c.name}</td>
                <td className={`${tdCls} font-mono text-xs`}>{c.code}</td>
                <td className={tdCls}>{c.currency}</td>
                <td className={tdCls}>{c.states?.length || 0} regions · {c.states?.reduce((n, s) => n + (s.cities?.length || 0), 0) || 0} cities</td>
                <td className={tdCls}>
                  <select
                    value={c.status}
                    onChange={(e) => setStatus(c, e.target.value)}
                    className="px-2.5 py-1.5 rounded-lg bg-sand/60 text-xs font-semibold outline-none"
                    data-testid={`country-status-${c.code}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </td>
                <td className={tdCls}>
                  <div className="flex gap-1">
                    <button onClick={() => { setForm({ ...EMPTY, ...c }); setEditing(c.id); }} className="p-2 text-clay hover:bg-sand rounded-lg" data-testid={`edit-country-${c.code}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(c)} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`delete-country-${c.code}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing === "new" ? "Add country" : `Edit — ${form.name}`} onClose={() => setEditing(null)} wide>
          <form onSubmit={save} className="space-y-5" data-testid="country-form">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Country name *"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="country-name-input" /></Field>
              <Field label="Code *"><input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={3} className={inputCls} data-testid="country-code-input" /></Field>
              <Field label="Currency"><input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className={inputCls} data-testid="country-currency-input" /></Field>
            </div>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="country-status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </Field>

            <div className="border border-sand rounded-xl p-4 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone2">States / Regions & Cities</p>
              <div className="flex gap-2">
                <input value={newState} onChange={(e) => setNewState(e.target.value)} placeholder="Add a state/region…" className={inputCls} data-testid="new-state-input" />
                <button type="button" onClick={addState} className="px-4 py-2 bg-ink text-cream text-xs font-semibold rounded-xl shrink-0" data-testid="add-state-btn">Add</button>
              </div>
              {form.states.map((s) => (
                <div key={s.name} className="bg-sand/50 rounded-xl p-4" data-testid={`state-block-${s.name.replace(/\s+/g, "-")}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-sm text-ink">{s.name}</p>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, states: f.states.filter((x) => x.name !== s.name) }))} className="text-ruby" data-testid={`remove-state-${s.name.replace(/\s+/g, "-")}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {s.cities.map((city) => (
                      <span key={city} className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E6DED3] px-2.5 py-1 rounded-full">
                        {city}
                        <button type="button" onClick={() => setForm((f) => ({ ...f, states: f.states.map((x) => x.name === s.name ? { ...x, cities: x.cities.filter((c) => c !== city) } : x) }))} data-testid={`remove-city-${s.name.replace(/\s+/g, "-")}-${city.replace(/\s+/g, "-")}`}>
                          <X className="w-3 h-3 text-ruby" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={cityInputs[s.name] || ""} onChange={(e) => setCityInputs((c) => ({ ...c, [s.name]: e.target.value }))} placeholder="Add city…" className={`${inputCls} !py-2`} data-testid={`add-city-input-${s.name.replace(/\s+/g, "-")}`} />
                    <button type="button" onClick={() => addCity(s.name)} className="px-3 py-2 bg-gold-light border border-gold/40 text-gold-dark text-xs font-semibold rounded-xl shrink-0" data-testid={`add-city-btn-${s.name.replace(/\s+/g, "-")}`}>Add</button>
                  </div>
                </div>
              ))}
            </div>
            <button type="submit" className="w-full bg-ruby text-cream font-bold py-3 rounded-xl" data-testid="country-save-btn">Save country</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
